import React, { useEffect, useMemo, useState } from "react";
import { LayoutDashboard, Package, ShoppingCart, Plus, Pencil, Trash2, TrendingUp, Users, DollarSign, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api, { money, formatApiError } from "../lib/api";
import ProductModal, { SHOP_CATEGORIES, shopCategoryOf } from "../components/admin/ProductModal";
import OrdersTable from "../components/admin/OrdersTable";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="gold-border rounded-xl bg-[#141416] p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="eyebrow text-[0.55rem]">{label}</span>
      <Icon size={16} className={accent || "text-[#D4AF37]"} />
    </div>
    <p className="font-serif-lux text-3xl text-[#FAFAF7]">{value}</p>
  </div>
);

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(null); // null | {} | product
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("All");

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data));

  useEffect(() => { loadStats(); loadProducts(); loadOrders(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      loadProducts(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };

  const setOrderStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Order status updated");
    loadOrders();
  };

  const visible = useMemo(() => products.filter((p) =>
    (catFilter === "All" || shopCategoryOf(p) === catFilter) &&
    (!query || `${p.name} ${p.brand}`.toLowerCase().includes(query.toLowerCase()))), [products, query, catFilter]);

  const chartData = products.slice(0, 8).map((p) => ({ name: p.name.split(" ")[0], stock: p.stock }));
  const TABS = [["overview", "Overview", LayoutDashboard], ["products", "Products", Package], ["orders", "Orders", ShoppingCart]];

  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-2">STORE MANAGEMENT</p>
            <h1 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7]">Admin Dashboard</h1>
          </div>
          <div className="flex gap-2">
            {TABS.map(([id, label, Icon]) => (
              <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm transition-colors ${tab === id ? "bg-[#D4AF37] text-[#0B0B0C]" : "gold-border text-[#FAFAF7]/70 hover:text-[#D4AF37]"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && stats && (
          <div className="space-y-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={DollarSign} label="REVENUE" value={money(stats.revenue)} />
              <StatCard icon={ShoppingCart} label="ORDERS" value={stats.orders_count} />
              <StatCard icon={TrendingUp} label="AVG ORDER" value={money(stats.avg_order_value)} />
              <StatCard icon={Users} label="CUSTOMERS" value={stats.customers_count} />
              <StatCard icon={Package} label="PRODUCTS" value={stats.products_count} />
              <StatCard icon={AlertTriangle} label="LOW STOCK" value={stats.low_stock} accent="text-[#58111A]" />
              <StatCard icon={ShoppingCart} label="PAID ORDERS" value={stats.paid_orders} />
            </div>
            <div className="gold-border rounded-xl bg-[#141416] p-6">
              <h3 className="font-serif-lux text-2xl text-[#FAFAF7] mb-6">Inventory by Product</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" stroke="#FAFAF7" tick={{ fontSize: 11, fill: "rgba(250,250,247,0.5)" }} />
                  <YAxis stroke="#FAFAF7" tick={{ fontSize: 11, fill: "rgba(250,250,247,0.5)" }} />
                  <Tooltip contentStyle={{ background: "#0B0B0C", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8 }} labelStyle={{ color: "#D4AF37" }} />
                  <Bar dataKey="stock" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill="#D4AF37" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                {["All", ...Object.keys(SHOP_CATEGORIES)].map((c) => (
                  <button key={c} data-testid={`admin-cat-filter-${c.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setCatFilter(c)}
                    className={`px-3.5 py-1.5 rounded-full text-xs transition-colors ${catFilter === c ? "bg-[#D4AF37] text-[#0B0B0C]" : "gold-border text-[#FAFAF7]/60 hover:text-[#D4AF37]"}`}>
                    {c} <span className="opacity-60">({c === "All" ? products.length : products.filter((p) => shopCategoryOf(p) === c).length})</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FAFAF7]/40" />
                  <input data-testid="admin-product-search" placeholder="Search products…" value={query} onChange={(e) => setQuery(e.target.value)} className="input-lux pl-9 py-2 text-sm w-52" />
                </div>
                <button data-testid="admin-add-product" onClick={() => setModal({})} className="btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-2 whitespace-nowrap">
                  <Plus size={16} /> Add Product
                </button>
              </div>
            </div>
            <div className="gold-border rounded-xl bg-[#141416] overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(212,175,55,0.18)] text-left">
                    <th className="px-4 py-3 eyebrow text-[0.55rem]">Product</th>
                    <th className="px-4 py-3 eyebrow text-[0.55rem]">Category</th>
                    <th className="px-4 py-3 eyebrow text-[0.55rem]">Price</th>
                    <th className="px-4 py-3 eyebrow text-[0.55rem]">Stock</th>
                    <th className="px-4 py-3 eyebrow text-[0.55rem]">Flags</th>
                    <th className="px-4 py-3 eyebrow text-[0.55rem] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#FAFAF7]/40">No products match</td></tr>}
                  {visible.map((p) => (
                    <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-[rgba(212,175,55,0.08)]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.images?.[0]} alt="" className="w-9 h-11 object-cover rounded" />
                          <div><p className="text-[#FAFAF7]">{p.name}</p><p className="text-xs text-[#FAFAF7]/40">{p.brand}</p></div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#FAFAF7]/60">{shopCategoryOf(p)}<span className="block text-xs text-[#FAFAF7]/35">{p.subcategory}</span></td>
                      <td className="px-4 py-3 text-[#D4AF37]">{money(p.price)}</td>
                      <td className="px-4 py-3"><span className={p.stock < 10 ? "text-[#58111A]" : "text-[#FAFAF7]/70"}>{p.stock}</span></td>
                      <td className="px-4 py-3 text-xs text-[#FAFAF7]/50">{[p.is_best_seller && "Best Seller", p.is_new_arrival && "New"].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button data-testid={`admin-edit-${p.id}`} onClick={() => setModal(p)} className="text-[#FAFAF7]/60 hover:text-[#D4AF37]"><Pencil size={15} /></button>
                          <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p)} className="text-[#FAFAF7]/60 hover:text-[#58111A]"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && <OrdersTable orders={orders} onStatus={setOrderStatus} />}
      </div>

      {modal !== null && (
        <ProductModal product={modal.id ? modal : null} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadProducts(); loadStats(); }} />
      )}
    </div>
  );
}
